import React, { useEffect, useState } from "react"
import { useParams, useHistory } from "react-router-dom"
import {
  ThemeProvider,
  CssBaseline,
  Paper,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core"
import { createMuiTheme } from "@material-ui/core/styles"
import { useSelector } from "react-redux"
import services from "../../services"
import UserApplicationComponent from "../../components/UserApplication/UserApplicationComponent"
import ApplicationWorkflow from "../../components/ApplicationWorkflow/ApplicationWorkflow"
import styles from "./AdminUserApplicationPage.module.scss"
import { ADMIN } from "../../helpers/userRoles"
import { paths } from "../../routes/paths"
import {
  getPlanById,
  nationalCorporation,
  regionalCorporation,
  elitePremium,
  elitPlus,
  elite,
  solopreneur,
  professional,
  investorsWealthManagementPlatinum,
  investorsWealthManagementGold,
  investorsWealthManagementSilver,
  b2tMember1,
  b2tMember2,
  b2tMember3,
  chairmanMembership,
} from "../../pages/SignUpPaywallPages/utils/subscriptionConfig"

const darkTheme = createMuiTheme({
  palette: {
    type: "dark",
  },
})

const AdminUserApplicationPage = () => {
  const { id } = useParams()
  const history = useHistory()
  const [application, setApplication] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const userInfo = useSelector((state) => state.user)
  const isAdmin = userInfo?.role === ADMIN

  // Redirect non-admin users to the regular page
  useEffect(() => {
    if (!isAdmin) {
      history.replace(`/user-application/${id}`)
    }
  }, [isAdmin, history, id])

  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) {
        setError("No application ID provided")
        setLoading(false)
        return
      }

      try {
        const response =
          await services.userApplicationServices.getUserApplication(id)
        if (response && response.data) {
          setApplication(response.data)
        } else {
          setError("Application data not found")
        }
      } catch (error) {
        console.error("Error fetching application:", error)
        if (error.response?.status === 401) {
          setError({
            title: "Access Denied",
            message:
              "This application is not accessible. If you believe this is an error, please contact our customer support team.",
            support: true,
          })
        } else {
          setError({
            title: "Error Loading Application",
            message:
              "We're unable to load the application details at this time. Please try again later or contact our customer support team if the issue persists.",
            support: true,
          })
        }
      } finally {
        setLoading(false)
      }
    }

    fetchApplication()
  }, [id])

  const handleAccept = async () => {
    // For provider + other, we need to select a plan first
    if (
      application.business_type === "company_provider" &&
      application.business_category === "other"
    ) {
      setOpenPlanDialog(true)
      return
    }

    // For all other applications, let the backend calculate the plan
    setIsProcessing(true)
    try {
      await services.userApplicationServices.acceptApplication(id)
      // Show success message and refresh
      setError(null)
      // Refresh the page to show updated status
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      setIsProcessing(false)
      setError({
        title: "Error Accepting Application",
        message:
          error.response?.data?.error || error.response?.data?.message ||
          "Failed to accept the application. Please try again or contact support if the issue persists.",
        support: true,
      })
    }
  }

  const handlePlanDialogOpen = () => {
    setOpenPlanDialog(true)
  }

  const handlePlanDialogClose = () => {
    setOpenPlanDialog(false)
  }

  const handlePlanSelect = (event) => {
    setSelectedPlan(event.target.value)
  }

  const handlePlanConfirm = async () => {
    if (!selectedPlan) {
      setError({
        title: "Plan Selection Required",
        message: "Please select a subscription plan before approving.",
        support: false,
      })
      return
    }

    setIsProcessing(true)
    try {
      await services.userApplicationServices.acceptApplication(id, selectedPlan)
      handlePlanDialogClose()
      // Show success and refresh
      setError(null)
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      setIsProcessing(false)
      setError({
        title: "Error Accepting Application",
        message:
          error.response?.data?.error || error.response?.data?.message ||
          "Failed to accept the application. Please try again or contact support if the issue persists.",
        support: true,
      })
    }
  }

  const handleRejectOpen = () => {
    setOpenRejectDialog(true)
  }

  const handleRejectClose = () => {
    setOpenRejectDialog(false)
    setRejectionNotes("")
  }

  const handleReject = async () => {
    setIsProcessing(true)
    try {
      await services.userApplicationServices.rejectApplication(id, rejectionNotes)
      handleRejectClose()
      // Show success and refresh
      setError(null)
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      setIsProcessing(false)
      setError({
        title: "Error Declining Application",
        message:
          error.response?.data?.error || error.response?.data?.message ||
          "Failed to decline the application. Please try again or contact support if the issue persists.",
        support: true,
      })
    }
  }

  const [requestMessage, setRequestMessage] = useState("")
  const [openRequestDialog, setOpenRequestDialog] = useState(false)
  const [rejectionNotes, setRejectionNotes] = useState("")
  const [openRejectDialog, setOpenRejectDialog] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  // State for subscription plan selection
  const [openPlanDialog, setOpenPlanDialog] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState("")

  // All available subscription plans (including admin-only plans)
  const subscriptionPlans = [
    nationalCorporation,
    regionalCorporation,
    elitePremium,
    elitPlus,
    elite,
    solopreneur,
    professional,
    investorsWealthManagementPlatinum,
    investorsWealthManagementGold,
    investorsWealthManagementSilver,
    b2tMember1,
    b2tMember2,
    b2tMember3,
    chairmanMembership, // Admin-only plan
  ]

  // Set empty plan only for company_provider with category 'other'
  // All other plan calculations are done on the backend
  useEffect(() => {
    if (application) {
      if (
        application.business_type === "company_provider" &&
        application.business_category === "other"
      ) {
        // For provider + other, don't set a default plan
        setSelectedPlan("")
      } else {
        // For all other types, we don't need to set a plan as it will be calculated on the backend
        setSelectedPlan("")
      }
    }
  }, [application])

  const handleRequestDetailsOpen = () => {
    setOpenRequestDialog(true)
  }

  const handleRequestDetailsClose = () => {
    setOpenRequestDialog(false)
    setRequestMessage("")
  }

  const handleRequestDetailsSend = async () => {
    if (!requestMessage.trim()) {
      setError({
        title: "Message Required",
        message: "Please provide a message explaining what additional documents or information are needed.",
        support: false,
      })
      return
    }

    setIsProcessing(true)
    try {
      await services.userApplicationServices.requestDetails(id, requestMessage)
      handleRequestDetailsClose()
      // Show success and refresh
      setError(null)
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      setIsProcessing(false)
      setError({
        title: "Error Requesting Details",
        message:
          error.response?.data?.error || error.response?.data?.message ||
          "Failed to request additional details. Please try again or contact support if the issue persists.",
        support: true,
      })
    }
  }

  const handleStatusTransition = async (newStatus, notes) => {
    try {
      if (newStatus === 'approved') {
        await services.userApplicationServices.acceptApplication(id)
      } else if (newStatus === 'rejected') {
        await services.userApplicationServices.rejectApplication(id, notes)
      } else if (newStatus === 'request_details') {
        await services.userApplicationServices.requestDetails(id, notes || 'Additional information required')
      } else {
        await services.userApplicationServices.updateApplication(id, { status: newStatus, notes: notes })
      }
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      setError({
        title: "Error Transitioning Status",
        message:
          error.response?.data?.error || error.response?.data?.message ||
          "Failed to transition application status. Please try again or contact support if the issue persists.",
        support: true,
      })
      throw error
    }
  }

  const handleErrorClose = () => {
    setError(null)
  }

  const handleContactSupport = async () => {
    try {
      const { openMarkerIO } = await import("../../services/markerIOService")
      await openMarkerIO(userInfo)
    } catch (error) {
      console.error("Failed to open Marker.io widget:", error)
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <div className={styles.container}>
        <div className={styles.content}>
          <Paper className={styles.paper}>
            {!loading && !error && application && (
              <>
                <div className={styles.adminHeader}>
                  <Typography variant="h4" gutterBottom>
                    Application Administration
                  </Typography>
                  <Typography paragraph className={styles.adminText}>
                    Review and manage user application details below.
                  </Typography>
                </div>

                {/* Application Workflow */}
                <ApplicationWorkflow
                  application={application}
                  onStatusTransition={handleStatusTransition}
                  loading={loading}
                />

                <Divider className={styles.divider} />

                <UserApplicationComponent
                  application={application}
                  loading={loading}
                  error={error}
                  handleErrorClose={handleErrorClose}
                  handleContactSupport={handleContactSupport}
                />

                {application.status === "request_details" &&
                  application.request_message && (
                    <div className={styles.requestMessageSection}>
                      <Typography
                        variant="h6"
                        className={styles.requestMessageTitle}
                      >
                        Request Message:
                      </Typography>
                      <div className={styles.requestMessage}>
                        {application.request_message}
                      </div>
                    </div>
                  )}

                <Divider className={styles.divider} />
                <div className={styles.buttonContainer}>
                  <Button
                    variant="contained"
                    onClick={handleAccept}
                    disabled={application.status === "approved" || isProcessing}
                    className={`${styles.actionButton} ${styles.accept}`}
                  >
                    {isProcessing ? "Processing..." : "Approve"}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handlePlanDialogOpen}
                    disabled={application.status === "approved" || isProcessing}
                    className={`${styles.actionButton} ${styles.accept}`}
                    style={{ marginLeft: "8px" }}
                  >
                    Approve with Plan...
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRejectOpen}
                    disabled={application.status === "rejected" || isProcessing}
                    className={`${styles.actionButton} ${styles.reject}`}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRequestDetailsOpen}
                    disabled={application.status === "request_details" || isProcessing}
                    className={`${styles.actionButton} ${styles.requestDetails}`}
                  >
                    Request Additional Documents
                  </Button>
                </div>
              </>
            )}

            {/* If there's an error or loading, the UserApplicationComponent will handle it */}
            {(loading || error || !application) && (
              <UserApplicationComponent
                application={application}
                loading={loading}
                error={error}
                handleErrorClose={handleErrorClose}
                handleContactSupport={handleContactSupport}
              />
            )}
          </Paper>
        </div>
      </div>

      {/* Request Details Dialog */}
      <Dialog
        open={openRequestDialog}
        onClose={handleRequestDetailsClose}
        aria-labelledby="request-details-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="request-details-dialog-title">
          Request Additional Documents
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please specify what additional documents or information you need from the applicant. 
            This message will be sent to the applicant via email.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="request-message"
            label="Request Message"
            placeholder="e.g., Please provide the following documents: Certificate of Incorporation, Tax ID documentation, Insurance certificates..."
            type="text"
            fullWidth
            multiline
            rows={6}
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            disabled={isProcessing}
            InputProps={{
              style: { color: "white" },
            }}
            InputLabelProps={{
              style: { color: "white" },
            }}
            helperText="Be specific about which documents or information are needed"
            FormHelperTextProps={{
              style: { color: "rgba(255, 255, 255, 0.7)" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRequestDetailsClose}
            style={{ color: "white" }}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestDetailsSend}
            color="primary"
            variant="contained"
            disabled={!requestMessage.trim() || isProcessing}
          >
            {isProcessing ? "Sending..." : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={handleRejectClose}
        aria-labelledby="reject-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="reject-dialog-title">
          Decline Application
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for declining this application. 
            This message will be sent to the applicant via email.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="rejection-notes"
            label="Decline Reason"
            placeholder="e.g., Application does not meet the required criteria, Missing required documentation, etc."
            type="text"
            fullWidth
            multiline
            rows={6}
            value={rejectionNotes}
            onChange={(e) => setRejectionNotes(e.target.value)}
            disabled={isProcessing}
            InputProps={{
              style: { color: "white" },
            }}
            InputLabelProps={{
              style: { color: "white" },
            }}
            helperText="Provide a clear explanation for the decline"
            FormHelperTextProps={{
              style: { color: "rgba(255, 255, 255, 0.7)" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRejectClose}
            style={{ color: "white" }}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="secondary"
            variant="contained"
            disabled={!rejectionNotes.trim() || isProcessing}
          >
            {isProcessing ? "Processing..." : "Confirm Decline"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Plan Selection Dialog */}
      <Dialog
        open={openPlanDialog}
        onClose={handlePlanDialogClose}
        aria-labelledby="plan-selection-dialog-title"
      >
        <DialogTitle id="plan-selection-dialog-title">
          Select Subscription Plan
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {application?.business_type === "company_provider" &&
            application?.business_category === "other"
              ? "Please select a subscription plan for this Provider Other application:"
              : "You can change the default subscription plan if needed:"}
          </DialogContentText>
          <FormControl fullWidth margin="dense">
            <InputLabel id="subscription-plan-label">
              Subscription Plan
            </InputLabel>
            <Select
              labelId="subscription-plan-label"
              id="subscription-plan"
              value={selectedPlan}
              onChange={handlePlanSelect}
              fullWidth
            >
              {subscriptionPlans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.title} {plan.tier ? `(${plan.tier})` : ""} -{" "}
                  {plan.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handlePlanDialogClose} 
            color="primary"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePlanConfirm}
            color="primary"
            variant="contained"
            disabled={!selectedPlan || isProcessing}
          >
            {isProcessing ? "Processing..." : "Approve with Selected Plan"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default AdminUserApplicationPage
